import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils.js';
import {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
  type ChangeEvent,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form, type FormikProps } from 'formik';
import shortid from 'shortid';
import { Text } from '@rsos/base-components';
import { Box, Button } from '@rsos/components/capstone/base';
import { useFlag } from '@rsos/flags';
import useWhichReverseGeocodeService from '@rsos/hooks/useWhichReverseGeocodeService';
import { selectExistingRequest } from '@rsos/reduxDataStore';
import {
  setFormPageToShow,
  setTemporaryLocation,
  setTentativeETALocation,
  resetPreselectedLandingZone,
} from '@rsos/reduxDataStore/src/features/airAmbulance/airAmbulanceSlice';
import { setSelectedTool } from '@rsos/reduxDataStore/src/features/eventPane/eventPaneSlice';
import {
  AIR_AMBULANCE_CARD,
  closeCard,
} from '@rsos/reduxDataStore/src/features/mapCard/mapCardSlice';
import {
  closeResourcePanel,
  setResourcePanelView,
  useBreadCrumbs,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import {
  trackOpenAirAmbulanceRequestForm,
  trackCloseAirAmbulanceRequestForm,
  trackLocationSelection,
  type LocationSelectionMethod,
} from '../../../gainsightTrackings/airAmbulanceTrackings';
import { needsWebMercatorConversion } from '../../../utils/needsWebMercatorConversion';
import usePermissionedGeocoder from '../../JurisdictionView/AviveAED/usePermissionedGeocoder';
import useAirAmbulanceEtlSubmit from '../etl/hooks/useAirAmbulanceEtlSubmit';
import useFormSubmit from '../midas/hooks/useFormSubmit';
import type {
  TentativeETA,
  ExistingRequest,
  Coordinates,
  SelectOption,
} from '../types';
import { ButtonContainer, Loader } from './AirAmbulanceFormStandalone.styles';
import SelectLocationBanner from './SelectLocationBanner/SelectLocationBanner';
import ContactSection from './components/ContactSection';
import LocationSection from './components/LocationSection';
import RequestSection from './components/RequestSection';
import type { GeocodedAddress } from './components/SuggestedAddress';
import {
  FORM_PAGES,
  PICKUP_COORDINATES,
  PICKUP_LOCATION,
  LAUNCH_TYPES,
  LANDING_ZONE_EXTERNAL_ID,
} from './constants';
import {
  useInitialFormValues,
  useDebouncedAddressUpdate,
  useFormReverseGeocode,
  useLandingZonesLayer,
} from './hooks';
import {
  isValidCoordinates,
  formatAddressFromObject,
  checkLaunchType,
} from './utils';
import {
  validateForm,
  formatCoordinates,
  parseCoordinates,
} from './validation';

interface AirAmbulanceState {
  airAmbulance: {
    preselectedLandingZone: SpatialObject | null;
  };
}

interface SpatialObject {
  id?: string | number;
  name?: string;
  external_id?: string;
  center?: {
    coordinates?: [number, number];
  };
}

interface FormValues {
  pickup_coordinates: string;
  pickup_location: string;
  pickup_details: string;
  ground_contact: string;
  patient_info: string;
  request_reason: string | SelectOption;
  multiple_units_requested: string;
  launch_type: string;
  air_service_declined: string;
  air_service_responding: string;
  landing_zone_external_id: string;
  [key: string]: string | SelectOption;
}

interface AirAmbulanceFormStandaloneProps {
  hasLocationToRender?: boolean;
  tentativeETA?: TentativeETA | null;
  fromMapCard?: boolean;
}

const AirAmbulanceFormStandalone = ({
  hasLocationToRender = false,
  tentativeETA = null,
  fromMapCard = false,
}: AirAmbulanceFormStandaloneProps): JSX.Element => {
  const dispatch = useDispatch();
  const breadCrumbs = useBreadCrumbs();
  const formikRef = useRef<FormikProps<FormValues>>(null);
  const existingRequest = useSelector(
    selectExistingRequest,
  ) as ExistingRequest | null;
  const preselectedLandingZone = useSelector(
    (state: AirAmbulanceState) => state.airAmbulance.preselectedLandingZone,
  );
  const isGmrLandingZoneEnabled = useFlag('gmr_landing_zone');
  const isEtlIntegrationEnabled = useFlag('air_ambulance_etl_integrations');
  const hasGenericResourceRequests = useFlag('generic_resource_requests');
  const { isEnabled, toggleLandingZoneLayer } = useLandingZonesLayer();

  // Only enable the landing zone layer once when the component mounts (if flag is enabled and layer is off)
  useEffect(() => {
    if (isGmrLandingZoneEnabled && !isEnabled) {
      toggleLandingZoneLayer(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGmrLandingZoneEnabled]);

  const requestId = useMemo(() => {
    return existingRequest?.request_id;
  }, [existingRequest]);

  const extTripId = useMemo(() => {
    return existingRequest?.body?.ext_trip_id;
  }, [existingRequest]);

  // Form state
  const [isLoading, setIsLoading] = useState(false);
  const [addressToGeocode, setAddressToGeocode] = useState('');
  const [coordsToReverseGeocode, setCoordsToReverseGeocode] =
    useState<Coordinates | null>(null);
  const [isAddressSelected, setIsAddressSelected] = useState(
    hasLocationToRender || !!existingRequest?.body, // set as default to prevent location error for call event or when editing
  );
  const [isAddressValid, setIsAddressValid] = useState(
    hasLocationToRender || !!existingRequest?.body, // set as default to prevent location error for call event or when editing
  );
  const [reverseGeocodeResults, setReverseGeocodeResults] = useState<
    GeocodedAddress[] | null
  >(null);
  const [isUpdatingFromCoords, setIsUpdatingFromCoords] = useState(false);
  const [isUpdatingFromAddress, setIsUpdatingFromAddress] = useState(false);
  const [selectedLandingZone, setSelectedLandingZone] =
    useState<SpatialObject | null>(preselectedLandingZone);

  const service = useWhichReverseGeocodeService();
  const geocodedResults: GeocodedAddress[] = usePermissionedGeocoder(
    addressToGeocode,
  ) as GeocodedAddress[];
  const { isReverseGeocoding, foundAddress } = useFormReverseGeocode({
    coordinates: coordsToReverseGeocode,
    service,
  });

  const debouncedSetAddress = useDebouncedAddressUpdate(setAddressToGeocode);

  const initialValues = useInitialFormValues() as FormValues;
  const UUID = useMemo(() => shortid.generate(), []);

  // Standard Midas submission hook
  const handleMidasSubmit = useFormSubmit(
    isLoading,
    setIsLoading,
    tentativeETA,
    UUID,
    fromMapCard,
  );

  // ETL submission hook (always called but only used when flag is enabled)
  const handleEtlSubmit = useAirAmbulanceEtlSubmit({
    isLoading,
    setIsLoading,
    UUID,
  });

  // Use ETL submission when flag is enabled, otherwise use Midas
  const handleSubmit = isEtlIntegrationEnabled
    ? handleEtlSubmit
    : handleMidasSubmit;

  useEffect(() => {
    if (existingRequest?.body) {
      if (initialValues.pickup_coordinates) {
        setIsAddressValid(true);
        setIsAddressSelected(true);

        // Track location auto-populated from existing request
        const coords = parseCoordinates(initialValues.pickup_coordinates);
        if (coords) {
          trackLocationSelection({
            requestId: extTripId || requestId,
            location: coords,
            selectionMethod: 'existing_request',
            uuid: UUID,
          });
        }

        // If we have coordinates but need to reverse geocode to get the address
        if (
          !initialValues.pickup_location &&
          initialValues.pickup_coordinates
        ) {
          if (coords) {
            setCoordsToReverseGeocode(coords);
          }
        }
      }
    } else {
      if (initialValues.pickup_coordinates) {
        const coords = parseCoordinates(initialValues.pickup_coordinates);
        if (coords) {
          setCoordsToReverseGeocode(coords);
          // Track location auto-populated from call location
          trackLocationSelection({
            requestId: extTripId || requestId,
            location: coords,
            selectionMethod: 'call_location',
            uuid: UUID,
          });
        }
      }
    }
  }, [existingRequest, initialValues, extTripId, requestId, UUID]);

  // Track the open event when the form is mounted
  useEffect(() => {
    trackOpenAirAmbulanceRequestForm({ uuid: UUID });
  }, [UUID]);

  // Clean up when component unmounts
  useEffect(() => {
    return () => {
      // Reset form page when component unmounts
      if (existingRequest) {
        dispatch(setFormPageToShow(FORM_PAGES.STATUS));
      }
      dispatch(setTemporaryLocation(null));
    };
  }, [dispatch, existingRequest]);

  const handleCancel = (): void => {
    if (hasGenericResourceRequests) {
      if (breadCrumbs.includes('list_requests') && breadCrumbs.length > 1) {
        dispatch(setResourcePanelView('list_requests'));
      } else {
        dispatch(closeResourcePanel());
      }
    } else {
      if (!hasLocationToRender) {
        dispatch(closeCard(AIR_AMBULANCE_CARD));
      }
      if (existingRequest) {
        dispatch(setFormPageToShow(FORM_PAGES.STATUS));
      } else {
        dispatch(setSelectedTool(null));
      }
    }

    trackCloseAirAmbulanceRequestForm();
  };

  const handleSelectSuggestion = useCallback(
    (suggestion: GeocodedAddress): void => {
      if (!formikRef.current) return;
      const { setFieldValue, setFieldTouched } = formikRef.current;

      setIsUpdatingFromAddress(true);
      const { x, y } = suggestion.location || {};

      let lat = y;
      let lng = x;

      if (
        typeof x === 'number' &&
        typeof y === 'number' &&
        needsWebMercatorConversion(x, y)
      ) {
        [lng, lat] = webMercatorUtils.xyToLngLat(x, y);
      }

      const coordinates = formatCoordinates(lat as number, lng as number);

      setFieldValue(PICKUP_LOCATION, suggestion.address);
      setFieldValue(PICKUP_COORDINATES, coordinates);
      setFieldTouched(PICKUP_LOCATION, true);
      setFieldTouched(PICKUP_COORDINATES, true);

      setIsAddressValid(true);
      setIsAddressSelected(true);
      setReverseGeocodeResults(null);
      setAddressToGeocode('');
      setIsUpdatingFromAddress(false);
      setCoordsToReverseGeocode({
        latitude: lat as number,
        longitude: lng as number,
      });

      // Track address-based selection explicitly
      trackLocationSelection({
        requestId: extTripId || requestId,
        location: { latitude: lat as number, longitude: lng as number },
        selectionMethod: 'address_input',
        uuid: UUID,
      });
    },
    [extTripId, requestId, UUID],
  );

  const handleCoordinateChange = useCallback(
    (
      e: ChangeEvent<HTMLInputElement> | { target: { value: string } },
      handleChange: (e: ChangeEvent<HTMLInputElement>) => void,
      selectionMethod: LocationSelectionMethod = 'coordinates_input',
    ): void => {
      const { value } = e.target;
      handleChange(e as ChangeEvent<HTMLInputElement>);
      setAddressToGeocode('');

      // Clear landing zone if coordinates are manually changed
      if (value && selectedLandingZone) {
        setSelectedLandingZone(null);
      }

      const coords = parseCoordinates(value);

      if (coords) {
        const { latitude, longitude } = coords;
        if (isValidCoordinates(latitude, longitude)) {
          setIsUpdatingFromCoords(true);
          setIsAddressValid(true);
          setIsAddressSelected(true);
          setCoordsToReverseGeocode(coords);
          trackLocationSelection({
            requestId: extTripId || requestId,
            location: coords,
            selectionMethod,
            uuid: UUID,
          });
        }
      } else {
        if (!value) {
          // If the field is cleared, reset the validation state
          setIsAddressValid(false);
          setIsAddressSelected(false);
        }
      }
    },
    [selectedLandingZone, extTripId, requestId, UUID],
  );

  const handleAddressChange = useCallback(
    (
      e: ChangeEvent<HTMLInputElement>,
      handleChange: (e: ChangeEvent<HTMLInputElement>) => void,
    ): void => {
      const { value } = e.target;
      handleChange(e);

      setIsUpdatingFromCoords(false);

      // Clear landing zone when user manually types an address
      if (value && selectedLandingZone) {
        setSelectedLandingZone(null);
      }

      if (value && value.length > 3) {
        debouncedSetAddress(value);
      } else {
        setReverseGeocodeResults(null);
        setAddressToGeocode('');
      }
    },
    [debouncedSetAddress, selectedLandingZone],
  );

  const handleLandingZoneSelect = useCallback(
    (
      selectedOption: SpatialObject | null,
      setFieldValue: (field: string, value: unknown) => void,
      handleChange: (e: ChangeEvent<HTMLInputElement>) => void,
    ): void => {
      const coordinates = selectedOption?.center?.coordinates;
      if (selectedOption && Array.isArray(coordinates)) {
        const [longitude, latitude] = coordinates;
        const latLng = `${latitude}, ${longitude}`;

        // Clear the address field when landing zone is selected
        setFieldValue(PICKUP_LOCATION, '');
        setFieldValue(PICKUP_COORDINATES, latLng);
        // Persist the selected landing zone external id for submit payload
        setFieldValue(
          LANDING_ZONE_EXTERNAL_ID,
          selectedOption?.external_id || '',
        );

        // Trigger coordinate change to populate address
        handleCoordinateChange({ target: { value: latLng } }, handleChange);

        // Set validation states
        setIsAddressValid(true);
        setIsAddressSelected(true);

        // Update selected landing zone state
        setSelectedLandingZone(selectedOption);
      } else {
        // Handle clearing the landing zone selection
        setSelectedLandingZone(null);
        // Clear stored landing zone external id when unselected
        setFieldValue(LANDING_ZONE_EXTERNAL_ID, '');
      }
    },
    [handleCoordinateChange],
  );

  useEffect(() => {
    if (coordsToReverseGeocode && isAddressValid) {
      dispatch(setTemporaryLocation(coordsToReverseGeocode));
      dispatch(setTentativeETALocation(coordsToReverseGeocode));
    } else {
      dispatch(setTemporaryLocation(null));
      dispatch(setTentativeETALocation(null));
    }
  }, [dispatch, coordsToReverseGeocode, isAddressValid]);

  // Handle preselected landing zone from map marker
  useEffect(() => {
    if (preselectedLandingZone && formikRef.current) {
      // Auto-select the landing zone and populate form fields
      handleLandingZoneSelect(
        preselectedLandingZone,
        formikRef.current.setFieldValue,
        formikRef.current.handleChange,
      );

      // Clear the preselected landing zone from Redux after using it
      dispatch(resetPreselectedLandingZone());
    }
  }, [preselectedLandingZone, handleLandingZoneSelect, dispatch]);

  // Handle geocoding results
  const handleGeocodingResults = useCallback(
    (values: FormValues): void => {
      if (!formikRef.current || !addressToGeocode || isUpdatingFromCoords)
        return;
      const { setFieldValue } = formikRef.current;

      if (!geocodedResults?.length) {
        setIsAddressValid(false);
      } else {
        const exactMatch = geocodedResults.find(
          result =>
            result.address?.toLowerCase() ===
            values.pickup_location.toLowerCase(),
        );

        setIsAddressValid(true);
        if (exactMatch) {
          setIsUpdatingFromAddress(true);
          const coordinates = formatCoordinates(
            exactMatch.location?.y,
            exactMatch.location?.x,
          );
          setFieldValue(PICKUP_COORDINATES, coordinates);

          setIsUpdatingFromAddress(false);
        }
      }
    },
    [addressToGeocode, geocodedResults, isUpdatingFromCoords],
  );

  // Handle reverse geocoding results
  const handleReverseGeocodingResults = useCallback(() => {
    if (!formikRef.current || !foundAddress || isUpdatingFromAddress) return;
    const { setFieldValue } = formikRef.current;

    const { address, addressObject } = foundAddress;
    if (!address && !addressObject) return;

    const formattedAddress = addressObject
      ? formatAddressFromObject(addressObject)
      : address;

    setFieldValue(PICKUP_LOCATION, formattedAddress);
    setReverseGeocodeResults([
      {
        address: formattedAddress,
        location: {
          y: coordsToReverseGeocode?.latitude,
          x: coordsToReverseGeocode?.longitude,
        },
      },
    ]);

    setIsUpdatingFromCoords(false);
  }, [foundAddress, isUpdatingFromAddress, coordsToReverseGeocode]);

  // Effect for reverse geocoding results
  useEffect(() => {
    if (foundAddress) {
      handleReverseGeocodingResults();
    }
  }, [foundAddress, handleReverseGeocodingResults]);

  const hasErrors = useCallback(
    (values: FormValues): boolean => {
      if (
        values?.ground_contact?.length === 0 ||
        values?.patient_info?.length === 0 ||
        values?.request_reason === 'Select' ||
        values?.air_service_declined === '' ||
        values?.air_service_responding === '' ||
        !isAddressSelected ||
        !isAddressValid
      ) {
        return true;
      }
      return false;
    },
    [isAddressSelected, isAddressValid],
  );

  return (
    <Box>
      <Box mb={3}>
        {tentativeETA?.providers && (
          <Text color="primaryText" size="small" italic>
            {tentativeETA.providers}
          </Text>
        )}
      </Box>
      <SelectLocationBanner />
      <Formik
        innerRef={formikRef}
        enableReinitialize
        initialValues={initialValues}
        validate={validateForm}
        validateOnMount
        validateOnChange={false}
        validateOnBlur
        onSubmit={handleSubmit}
      >
        {formikProps => {
          const {
            values,
            touched,
            handleBlur,
            handleChange,
            setFieldValue,
            setFieldTouched,
          } = formikProps;
          const secondaryLaunchTypeSelected = checkLaunchType(
            values.launch_type,
          );

          return (
            <Form
              id="airAmbulanceForm"
              onChange={() => handleGeocodingResults(values)}
            >
              <Box mx="5px">
                <LocationSection
                  values={values}
                  touched={touched}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  setIsAddressValid={setIsAddressValid}
                  setIsAddressSelected={setIsAddressSelected}
                  setAddressToGeocode={setAddressToGeocode}
                  isAddressValid={isAddressValid}
                  isReverseGeocoding={isReverseGeocoding}
                  geocodedResults={geocodedResults}
                  isAddressSelected={isAddressSelected}
                  reverseGeocodeResults={reverseGeocodeResults || undefined}
                  handleSelectSuggestion={handleSelectSuggestion}
                  handleAddressChange={handleAddressChange}
                  handleCoordinateChange={handleCoordinateChange}
                  handleLandingZoneSelect={handleLandingZoneSelect}
                  selectedLandingZone={selectedLandingZone}
                  requestId={extTripId || requestId}
                />

                <ContactSection
                  values={values}
                  touched={touched}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                />

                <RequestSection
                  values={values}
                  touched={touched}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                />

                <Box pt={4}>
                  <ButtonContainer horizontal>
                    <Button
                      grow
                      btnType="tertiary"
                      type="button"
                      onClick={handleCancel}
                      disabled={isLoading}
                    >
                      Cancel
                    </Button>
                    <Button
                      grow
                      type="submit"
                      btnType="primary"
                      disabled={isLoading || hasErrors(values)}
                    >
                      {isLoading ? (
                        <Loader />
                      ) : secondaryLaunchTypeSelected === LAUNCH_TYPES.TEST ? (
                        'Submit Test'
                      ) : secondaryLaunchTypeSelected ===
                        LAUNCH_TYPES.STANDBY ? (
                        'Submit Standby'
                      ) : (
                        'Submit'
                      )}
                    </Button>
                  </ButtonContainer>
                </Box>
              </Box>
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default AirAmbulanceFormStandalone;
